import { useState, useCallback } from 'react';
import { Header, Button, EmptyState } from '../../shared/components';
import { DataTable, Column } from '../../shared/components/DataTable';
import { Chip } from '../../shared/components/Chip';
import { apiRequest } from '../../shared/services/http';
import { getAuthToken } from '../../shared/auth/tokenProvider';

// ─── Tipos de respuesta del API emulador ──────────────────────────────────

interface EmuladorStatus {
  hasTestData: boolean;
  counts: { users: number; employees: number; records: number; overtime: number };
  note: string;
}

interface PayDto { extras: number; recargoNocturno: number; festivos: number; total: number }

interface OtRow {
  overtimeId: string;
  workDate: string;
  status: string;
  proposedMinutes: number;
  approvedMinutes: number | null;
  employeeNote: string | null;
}

interface EmpleadoLiquidacion {
  userId: string;
  fullName: string;
  documentNumber: string | null;
  itemNo: number | null;
  process: string | null;
  salary: number | null;
  netHoras: number;
  extrasTotal: number;
  recargoNocturno: number;
  festivoHoras: number;
  pay: PayDto | null;
  overtime: OtRow[];
  escenarios: string[];
  openShifts: number;
  isTest: true;
}

interface LiquidacionesResponse {
  from: string;
  to: string;
  generatedAt: string;
  weeklyHours: number;
  timeZone: string;
  totalTestEmployees: number;
  employees: EmpleadoLiquidacion[];
}

interface MarcacionRow {
  recordId: string;
  userId: string;
  fullName: string;
  recordType: 'ENTRADA' | 'SALIDA';
  recordTimestamp: string;
  declaredWorkMode: string | null;
}

interface MarcacionesResponse { total: number; records: MarcacionRow[] }

interface OvertimeRow {
  overtimeId: string;
  userId: string;
  fullName: string;
  workDate: string;
  scheduledMinutes: number;
  workedMinutes: number;
  excessMinutes: number;
  proposedMinutes: number;
  approvedMinutes: number | null;
  status: string;
  employeeNote: string | null;
  reviewedBy: string | null;
}

interface OvertimeResponse { total: number; overtime: OvertimeRow[] }

// ─── Helpers de formato ───────────────────────────────────────────────────

function cop(n: number | null | undefined) {
  if (n == null) return '–';
  return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n);
}
function h(n: number) { return n.toFixed(1) + ' h'; }
function m(n: number) { return Math.round(n) + ' min'; }

const STATUS_COLOR: Record<string, string> = {
  APROBADA:  'bg-green-100 text-green-800',
  PROCESADA: 'bg-blue-100 text-blue-800',
  ENVIADA:   'bg-cyan-100 text-cyan-800',
  SOLICITADA:'bg-yellow-100 text-yellow-800',
  RECHAZADA: 'bg-red-100 text-red-800',
  DECLINADA: 'bg-gray-100 text-gray-600',
};

function StatusBadge({ status }: { status: string }) {
  const cls = STATUS_COLOR[status] ?? 'bg-gray-100 text-gray-700';
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cls}`}>{status}</span>;
}

function TestBadge() {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold bg-orange-100 text-orange-700 border border-orange-300">
      🔬 TEST
    </span>
  );
}

// ─── Pestaña Liquidaciones ────────────────────────────────────────────────

function exportLiquidacionesExcel(data: LiquidacionesResponse) {
  const BOM = '﻿';
  const headers = [
    'Nombre', 'Documento', 'Proceso', 'Ítem', 'Salario',
    'Horas Netas', 'H.E. Total', 'Rec. Nocturno h', 'Festivo h',
    'Extras $', 'Rec. Noct. $', 'Festivo $', 'Total Liquidado $',
    'Estado OT', 'Escenario',
  ];
  const rows = data.employees.map((e) => [
    e.fullName,
    e.documentNumber ?? '',
    e.process ?? '',
    e.itemNo ?? '',
    e.salary ?? '',
    e.netHoras,
    e.extrasTotal,
    e.recargoNocturno,
    e.festivoHoras,
    e.pay?.extras ?? '',
    e.pay?.recargoNocturno ?? '',
    e.pay?.festivos ?? '',
    e.pay?.total ?? '',
    e.overtime.map((ot) => `${ot.status} ${ot.workDate}`).join(' | ') || 'Sin OT',
    e.escenarios?.join(', ') ?? '',
  ]);
  const csv = [headers, ...rows]
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(';'))
    .join('\r\n');
  const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `liquidaciones_test_${data.from}_${data.to}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function LiquidacionesTab() {
  const [from, setFrom] = useState('2026-10-05');
  const [to,   setTo]   = useState('2026-10-13');
  const [data, setData] = useState<LiquidacionesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getAuthToken();
      const res = await apiRequest<LiquidacionesResponse>(
        `/api/emulador/liquidaciones?from=${from}&to=${to}`,
        { token }
      );
      setData(res);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al cargar liquidaciones');
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  const columns: Column<EmpleadoLiquidacion>[] = [
    { key: 'fullName', header: 'Empleado', render: (r) => (
      <div>
        <div className="font-medium text-sm">{r.fullName}</div>
        <div className="text-xs text-gray-500">CC {r.documentNumber} · {r.process ?? '–'}</div>
      </div>
    )},
    { key: 'salary',          header: 'Salario',       render: (r) => cop(r.salary) },
    { key: 'netHoras',        header: 'Horas netas',   render: (r) => h(r.netHoras) },
    { key: 'extrasTotal',     header: 'H.E. total',    render: (r) => h(r.extrasTotal) },
    { key: 'recargoNocturno', header: 'Rec. noct.',    render: (r) => h(r.recargoNocturno) },
    { key: 'festivoHoras',    header: 'Festivo h',     render: (r) => h(r.festivoHoras) },
    { key: 'payExtras',       header: 'Extras $',      render: (r) => r.pay ? cop(r.pay.extras) : '–' },
    { key: 'payRecargo',      header: 'Rec. noct. $',  render: (r) => r.pay ? cop(r.pay.recargoNocturno) : '–' },
    { key: 'payFestivo',      header: 'Festivo $',     render: (r) => r.pay ? cop(r.pay.festivos) : '–' },
    { key: 'payTotal',        header: 'Total liquidado', render: (r) => (
      <span className="font-semibold text-green-700">{r.pay ? cop(r.pay.total) : '–'}</span>
    )},
    { key: 'overtime', header: 'Estado OT', render: (r) => (
      <div className="flex flex-col gap-1">
        {r.overtime.length === 0
          ? <span className="text-xs text-gray-400">Sin OT</span>
          : r.overtime.map((ot) => (
              <div key={ot.overtimeId} className="flex items-center gap-1 text-xs">
                <StatusBadge status={ot.status} />
                <span className="text-gray-500">{ot.workDate}</span>
              </div>
            ))
        }
      </div>
    )},
  ];

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-ink-100 bg-surface p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Desde</label>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="border rounded px-2 py-1 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Hasta</label>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="border rounded px-2 py-1 text-sm"
            />
          </div>
          <Button onClick={load} disabled={loading} variant="primary" size="md">
            {loading ? 'Calculando…' : 'Calcular liquidaciones'}
          </Button>
          {data && (
            <Button onClick={() => exportLiquidacionesExcel(data)} variant="secondary" size="md">
              ⬇️ Descargar Excel
            </Button>
          )}
        </div>
        {data && (
          <div className="mt-2 text-xs text-gray-500 flex flex-wrap gap-4">
            <span>📅 {data.from} → {data.to}</span>
            <span>👥 {data.totalTestEmployees} empleados TEST</span>
            <span>⏱ Semana legal: {data.weeklyHours} h</span>
            <span>🕐 {data.timeZone}</span>
          </div>
        )}
      </div>

      {error && <div className="rounded bg-red-50 border border-red-200 p-3 text-sm text-red-700">{error}</div>}

      {data ? (
        data.employees.length === 0 ? (
          <EmptyState title="Sin datos" description="No hay empleados de prueba con datos en el período seleccionado." />
        ) : (
          <DataTable<EmpleadoLiquidacion>
            columns={columns}
            rows={data.employees}
            rowKey={(r) => r.userId}
            emptyTitle="Sin empleados TEST"
          />
        )
      ) : (
        <EmptyState
          title="Selecciona el rango"
          description="Selecciona el rango de fechas y presiona «Calcular liquidaciones» para ver los resultados."
        />
      )}
    </div>
  );
}

// ─── Pestaña Marcaciones ──────────────────────────────────────────────────

function MarcacionesTab() {
  const [data, setData] = useState<MarcacionesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getAuthToken();
      const res = await apiRequest<MarcacionesResponse>('/api/emulador/marcaciones', { token });
      setData(res);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al cargar marcaciones');
    } finally {
      setLoading(false);
    }
  }, []);

  const columns: Column<MarcacionRow>[] = [
    { key: 'fullName',   header: 'Empleado',   render: (r) => r.fullName },
    { key: 'recordType', header: 'Tipo',        render: (r) => (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${r.recordType === 'ENTRADA' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
        {r.recordType}
      </span>
    )},
    { key: 'recordTimestamp', header: 'Timestamp', render: (r) =>
      new Date(r.recordTimestamp).toLocaleString('es-CO', { timeZone: 'America/Bogota', hour12: false })
    },
    { key: 'declaredWorkMode', header: 'Modalidad', render: (r) => r.declaredWorkMode ?? '–' },
  ];

  return (
    <div className="space-y-4">
      <div className="flex gap-3 items-center">
        <Button onClick={load} disabled={loading} variant="primary" size="md">
          {loading ? 'Cargando…' : 'Cargar marcaciones TEST'}
        </Button>
        {data && <span className="text-sm text-gray-500">{data.total} registros</span>}
      </div>
      {error && <div className="rounded bg-red-50 border border-red-200 p-3 text-sm text-red-700">{error}</div>}
      {data ? (
        data.records.length === 0
          ? <EmptyState title="Sin marcaciones" description="No hay marcaciones de prueba cargadas." />
          : <DataTable<MarcacionRow>
              columns={columns}
              rows={data.records}
              rowKey={(r) => r.recordId}
              emptyTitle="Sin marcaciones TEST"
            />
      ) : (
        <EmptyState
          title="Sin datos cargados"
          description="Presiona «Cargar marcaciones TEST» para ver los registros de entrada y salida de los 30 empleados de prueba."
        />
      )}
    </div>
  );
}

// ─── Pestaña TurnoGeo Extras ──────────────────────────────────────────────

function TurnoGeoExtrasTab() {
  const [data, setData] = useState<OvertimeResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getAuthToken();
      const res = await apiRequest<OvertimeResponse>('/api/emulador/overtime', { token });
      setData(res);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al cargar overtime');
    } finally {
      setLoading(false);
    }
  }, []);

  const columns: Column<OvertimeRow>[] = [
    { key: 'fullName',        header: 'Empleado',   render: (r) => <span className="font-medium text-sm">{r.fullName}</span> },
    { key: 'workDate',        header: 'Fecha',       render: (r) => r.workDate },
    { key: 'status',          header: 'Estado',      render: (r) => <StatusBadge status={r.status} /> },
    { key: 'workedMinutes',   header: 'Trabajado',   render: (r) => m(r.workedMinutes) },
    { key: 'excessMinutes',   header: 'Exceso',      render: (r) => m(r.excessMinutes) },
    { key: 'proposedMinutes', header: 'Propuesto',   render: (r) => m(r.proposedMinutes) },
    { key: 'approvedMinutes', header: 'Aprobado',    render: (r) => r.approvedMinutes != null ? m(r.approvedMinutes) : '–' },
    { key: 'employeeNote',    header: 'Escenario',   render: (r) => <span className="text-xs text-gray-600">{r.employeeNote ?? '–'}</span> },
    { key: 'reviewedBy',      header: 'Revisó',      render: (r) => r.reviewedBy ?? '–' },
  ];

  const byStatus = data?.overtime.reduce<Record<string, number>>((acc, r) => {
    acc[r.status] = (acc[r.status] ?? 0) + 1;
    return acc;
  }, {}) ?? {};

  return (
    <div className="space-y-4">
      <div className="flex gap-3 flex-wrap items-center">
        <Button onClick={load} disabled={loading} variant="primary" size="md">
          {loading ? 'Cargando…' : 'Cargar extras TEST'}
        </Button>
        {data && (
          <div className="flex gap-2 flex-wrap">
            {Object.entries(byStatus).map(([s, c]) => (
              <span key={s} className="text-xs flex items-center gap-1">
                <StatusBadge status={s} />
                <span className="text-gray-500">×{c}</span>
              </span>
            ))}
          </div>
        )}
      </div>
      {error && <div className="rounded bg-red-50 border border-red-200 p-3 text-sm text-red-700">{error}</div>}
      {data ? (
        data.overtime.length === 0
          ? <EmptyState title="Sin overtime" description="No hay registros de overtime de prueba." />
          : <DataTable<OvertimeRow>
              columns={columns}
              rows={data.overtime}
              rowKey={(r) => r.overtimeId}
              emptyTitle="Sin registros OT TEST"
            />
      ) : (
        <EmptyState
          title="Sin datos cargados"
          description="Presiona «Cargar extras TEST» para ver los 30 escenarios de horas extra cargados en la BD."
        />
      )}
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────

export function EmuladorPage() {
  const [tab, setTab] = useState<'liquidaciones' | 'marcaciones' | 'extras'>('liquidaciones');
  const [status, setStatus] = useState<EmuladorStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [seedMsg, setSeedMsg] = useState<string | null>(null);
  const [cleaning, setCleaning] = useState(false);
  const [cleanMsg, setCleanMsg] = useState<string | null>(null);

  const checkStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const token = await getAuthToken();
      const res = await apiRequest<EmuladorStatus>('/api/emulador/status', { token });
      setStatus(res);
    } catch {
      // ignore
    } finally {
      setStatusLoading(false);
    }
  }, []);

  const handleSembrar = useCallback(async () => {
    setSeedMsg(null);
    setCleanMsg(null);
    setSeeding(true);
    try {
      const token = await getAuthToken();
      const res = await apiRequest<{ message: string; created: Record<string, number> }>(
        '/api/emulador/sembrar',
        { method: 'POST', token },
      );
      setSeedMsg(res.message);
      // Refresca el estado automáticamente para mostrar conteos
      const token2 = await getAuthToken();
      const fresh = await apiRequest<EmuladorStatus>('/api/emulador/status', { token: token2 });
      setStatus(fresh);
    } catch (e: unknown) {
      setSeedMsg('Error: ' + (e instanceof Error ? e.message : 'No se pudo sembrar datos'));
    } finally {
      setSeeding(false);
    }
  }, []);

  const handleLimpiar = useCallback(async () => {
    if (!window.confirm('¿Seguro que deseas eliminar TODOS los datos de prueba? Esta acción es irreversible.')) return;
    setCleaning(true);
    setCleanMsg(null);
    setSeedMsg(null);
    try {
      const token = await getAuthToken();
      const res = await apiRequest<{ message: string }>('/api/emulador/limpiar', { method: 'DELETE', token });
      setCleanMsg(res.message);
      setStatus(null);
    } catch (e: unknown) {
      setCleanMsg('Error: ' + (e instanceof Error ? e.message : 'No se pudo limpiar'));
    } finally {
      setCleaning(false);
    }
  }, []);

  return (
    <div>
      <Header
        title="Emulador de Pruebas"
        subtitle="Sistema de datos sintéticos para validar el funcionamiento completo de TurnoGeo"
        right={<TestBadge />}
      />

      <div className="space-y-4 p-4">
        {/* Barra de estado y controles */}
        <div className="rounded-2xl border border-ink-100 bg-surface p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-ink-900">
                🔬 30 empleados de prueba · 30 escenarios de recargo · datos en tablas reales
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                Los empleados TEST son visibles en <strong>todas</strong> las pantallas: Resumen, Registro diario, Horas de TurnoGeo y Empleados.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={checkStatus} disabled={statusLoading} size="md">
                {statusLoading ? 'Verificando…' : '🔍 Verificar datos'}
              </Button>
              <Button variant="primary" onClick={handleSembrar} disabled={seeding} size="md">
                {seeding ? 'Sembrando…' : '🌱 Sembrar datos TEST'}
              </Button>
              <Button variant="danger" onClick={handleLimpiar} disabled={cleaning} size="md">
                {cleaning ? 'Limpiando…' : '🗑️ Limpiar datos TEST'}
              </Button>
            </div>
          </div>

          {status && (
            <div className="mt-3 pt-3 border-t border-ink-100 flex flex-wrap gap-4 text-sm">
              <span className={status.hasTestData ? 'text-green-600 font-medium' : 'text-gray-500'}>
                {status.hasTestData ? '✅ Datos cargados' : '❌ Sin datos de prueba'}
              </span>
              {status.hasTestData && (
                <>
                  <span>👤 {status.counts.users} usuarios</span>
                  <span>🏢 {status.counts.employees} empleados</span>
                  <span>📋 {status.counts.records} marcaciones</span>
                  <span>⏱ {status.counts.overtime} registros OT</span>
                </>
              )}
            </div>
          )}

          {seedMsg && (
            <div className={`mt-3 rounded p-2 text-sm ${seedMsg.startsWith('Error') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
              {seedMsg}
            </div>
          )}

          {cleanMsg && (
            <div className={`mt-3 rounded p-2 text-sm ${cleanMsg.startsWith('Error') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
              {cleanMsg}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2">
          <Chip
            label="💰 Liquidaciones"
            active={tab === 'liquidaciones'}
            onClick={() => setTab('liquidaciones')}
          />
          <Chip
            label="📍 Marcaciones"
            active={tab === 'marcaciones'}
            onClick={() => setTab('marcaciones')}
          />
          <Chip
            label="⏰ TurnoGeo Extras"
            active={tab === 'extras'}
            onClick={() => setTab('extras')}
          />
        </div>

        {tab === 'liquidaciones' && <LiquidacionesTab />}
        {tab === 'marcaciones'   && <MarcacionesTab />}
        {tab === 'extras'        && <TurnoGeoExtrasTab />}
      </div>
    </div>
  );
}
